import { useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import type { Reservation } from '@luxuryescapes/contract-svc-reservation';

import { MAX_SVC_IMAGE_UPLOAD_BYTES, getUploadedImageId, uploadImage } from '~/services/ImageService';
import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import { type LocalImageEdit, buildDefaultEdit } from './ImagesVideo/ImageEditor/ImageEditorUtils';
import type { MediaImage, PropertyRoomTypeWithImages, RoomOption, RoomSelection } from './ImagesVideo/types';
import {
  DEFAULT_ROOM_NAME,
  DEFAULT_SOURCE_ROOM_NAME,
  UNASSIGNED_IMAGE_LABEL,
  allSettledWithConcurrency,
  buildMediaImages,
  buildUploadPlans,
  canPersistRoomImageHiddenState,
  dedupeMediaImages,
  getPrimaryVideoId,
  getRoomKey,
  isAllowedImageFile,
} from './ImagesVideo/utils';
import type { UploadRoomAssignment, UploadedRoomAssignment } from './ImagesVideo/utils';

const IMAGE_UPLOAD_CONCURRENCY = 5;
const UNASSIGNED_ROOM_OPTION: RoomSelection = { roomName: DEFAULT_ROOM_NAME };

interface UseOfferBuilderMediaParams {
  images?: Array<App.OfferImage>;
  propertyImages?: Array<Reservation.Image>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  videos?: {
    luxuryescapes?: {
      vimeo_id?: string | null;
    };
  };
}

function removeImageOverride(overrides: Record<string, boolean>, publicId: string) {
  const { [publicId]: _removedOverride, ...nextOverrides } = overrides;

  return nextOverrides;
}

interface UploadFilesOptions {
  ignoreNonImageFiles?: boolean;
  roomAssignment?: UploadRoomAssignment;
}

export interface OfferBuilderMediaController {
  deletedImagePublicIds: Array<string>;
  hiddenImagePublicIds: Array<string>;
  imageEdits: Record<string, LocalImageEdit>;
  imagePendingDeletion?: MediaImage;
  isUploadingImages: boolean;
  mediaImages: Array<MediaImage>;
  roomImagePendingEdit?: MediaImage;
  roomOptions: Array<RoomOption>;
  uploadedRoomAssignments: Array<UploadedRoomAssignment>;
  videoId?: string;
  closeDeleteDialog: () => void;
  confirmDeleteImage: () => Promise<void>;
  deleteRoomImageFromEditor: (image: MediaImage) => void;
  deleteImage: (publicId: string) => Promise<void>;
  removeImageFromRoom: (publicId: string) => Promise<void>;
  saveImageEdit: (publicId: string, edit: LocalImageEdit) => void;
  setRoomImagePendingEdit: (image?: MediaImage) => void;
  setVideoId: (videoId?: string) => void;
  toggleHiddenImage: (publicId: string) => Promise<void>;
  updateImageTitle: (publicId: string, title: string) => void;
  uploadFiles: (files: Array<File>, options?: UploadFilesOptions) => Promise<void>;
  uploadFilesToRoom: (roomId: string, roomName: string, files: Array<File>) => Promise<void>;
  assignImagesToRoom: (roomId: string, roomName: string, selectedImages: Array<MediaImage>) => void;
  editRoomImage: (publicId: string) => void;
}

export default function useOfferBuilderMedia({
  images = [],
  propertyImages,
  propertyRoomTypes,
  videos,
}: UseOfferBuilderMediaParams): OfferBuilderMediaController {
  const { enqueueSnackbar } = useSnackbar();
  const [uploadedImages, setUploadedImages] = useState<Array<MediaImage>>([]);
  const [uploadedRoomAssignments, setUploadedRoomAssignments] = useState<Array<UploadedRoomAssignment>>([]);
  const [deletedImagePublicIds, setDeletedImagePublicIds] = useState<Array<string>>([]);
  const [removedRoomImagePublicIds, setRemovedRoomImagePublicIds] = useState<Array<string>>([]);
  const [hiddenImageOverrides, setHiddenImageOverrides] = useState<Record<string, boolean>>({});
  const [imagePendingDeletion, setImagePendingDeletion] = useState<MediaImage | undefined>();
  const [roomImagePendingEdit, setRoomImagePendingEdit] = useState<MediaImage | undefined>();
  const [imageEdits, setImageEdits] = useState<Record<string, LocalImageEdit>>({});
  const [imageRoomOverrides, setImageRoomOverrides] = useState<Record<string, RoomSelection>>({});
  const [localVideoId, setLocalVideoId] = useState<string | undefined>();
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const hiddenImageUpdatePublicIdsRef = useRef<Set<string>>(new Set());
  const roomImageDeletionPromisesRef = useRef<Map<string, Promise<void>>>(new Map());
  const videoId = localVideoId === '' ? undefined : (localVideoId ?? getPrimaryVideoId(videos));

  const mediaImages = useMemo(() => {
    const deletedImagePublicIdSet = new Set(deletedImagePublicIds);
    const removedRoomImagePublicIdSet = new Set(removedRoomImagePublicIds);

    return dedupeMediaImages(
      buildMediaImages({
        offerImages: images,
        propertyImages,
        propertyRoomTypes,
      }).concat(uploadedImages),
    )
      .filter((image) => !deletedImagePublicIdSet.has(image.publicId))
      .map((image) => {
        const imageEdit = imageEdits[image.publicId];
        const imageRoomOverride = imageRoomOverrides[image.publicId];
        const isRemovedRoomImage = removedRoomImagePublicIdSet.has(image.publicId);

        return {
          ...image,
          hidden: isRemovedRoomImage ? false : image.hidden,
          sourceLabel: imageRoomOverride?.roomName ?? imageEdit?.roomName ?? image.sourceLabel,
          sourceImageId: isRemovedRoomImage ? undefined : image.sourceImageId,
          sourcePropertyId: isRemovedRoomImage ? undefined : image.sourcePropertyId,
          sourceRoomId: imageRoomOverride ? imageRoomOverride.roomId : (imageEdit?.roomId ?? image.sourceRoomId),
          sourceRoomTypeId: isRemovedRoomImage ? undefined : image.sourceRoomTypeId,
          title: imageEdit?.title ?? image.title,
        };
      });
  }, [
    deletedImagePublicIds,
    imageEdits,
    imageRoomOverrides,
    images,
    propertyImages,
    propertyRoomTypes,
    removedRoomImagePublicIds,
    uploadedImages,
  ]);

  const hiddenImagePublicIds = useMemo(
    () =>
      mediaImages
        .filter((image) => hiddenImageOverrides[image.publicId] ?? image.hidden)
        .map((image) => image.publicId),
    [hiddenImageOverrides, mediaImages],
  );

  const roomOptions = useMemo<Array<RoomOption>>(
    () =>
      propertyRoomTypes?.map((room, index) => ({
        roomId: getRoomKey(room, index),
        roomName: room.name || DEFAULT_SOURCE_ROOM_NAME,
      })) ?? [],
    [propertyRoomTypes],
  );

  const uploadFiles = async (files: Array<File>, options: UploadFilesOptions = {}) => {
    const candidateFiles = options.ignoreNonImageFiles ? files.filter(isAllowedImageFile) : files;
    const uploadableFiles = candidateFiles.filter(
      (file) => isAllowedImageFile(file) && file.size <= MAX_SVC_IMAGE_UPLOAD_BYTES,
    );
    const rejectedFileCount = candidateFiles.length - uploadableFiles.length;

    if (rejectedFileCount > 0) {
      enqueueSnackbar(
        `${rejectedFileCount} ${rejectedFileCount === 1 ? 'file was' : 'files were'} skipped. Use supported images under 5MB.`,
        { variant: 'warning' },
      );
    }

    if (uploadableFiles.length === 0) {
      return;
    }

    setIsUploadingImages(true);

    try {
      const uploadPlans = buildUploadPlans({
        files: uploadableFiles,
        propertyRoomTypes,
        roomAssignment: options.roomAssignment,
      }).map((uploadPlan, index) => ({ ...uploadPlan, uploadIndex: index }));

      const uploadResults = await allSettledWithConcurrency(
        uploadPlans,
        IMAGE_UPLOAD_CONCURRENCY,
        async ({
          file,
          roomAssignment,
          uploadIndex,
        }): Promise<{ image: MediaImage; roomAssignment?: UploadedRoomAssignment }> => {
          const response = await uploadImage(file);
          const uploadedImageId = getUploadedImageId(response);

          if (!uploadedImageId) {
            throw new Error('Image service did not return an image id');
          }

          let persistedRoomImage: Awaited<ReturnType<typeof createPersistedRoomImage>> | undefined;

          if (roomAssignment) {
            try {
              persistedRoomImage = await createPersistedRoomImage({
                propertyRoomTypes,
                publicId: uploadedImageId,
                roomAssignment,
                uploadIndex,
              });
            } catch (error) {
              if (error instanceof Error) {
                reportError(error);
              }
            }
          }

          return {
            image: {
              hidden: persistedRoomImage?.hidden,
              order: persistedRoomImage?.order,
              publicId: uploadedImageId,
              sourceLabel: roomAssignment?.roomName ?? UNASSIGNED_IMAGE_LABEL,
              sourceImageId: persistedRoomImage?.id,
              sourcePropertyId: persistedRoomImage?.sourcePropertyId,
              sourceRoomId: roomAssignment?.roomId,
              sourceRoomTypeId: persistedRoomImage?.sourceRoomTypeId,
              title: persistedRoomImage?.title || undefined,
            },
            roomAssignment: roomAssignment
              ? {
                  ...roomAssignment,
                  publicId: uploadedImageId,
                }
              : undefined,
          };
        },
      );
      const uploadedMediaImages: Array<MediaImage> = [];
      const newUploadedRoomAssignments: Array<UploadedRoomAssignment> = [];

      uploadResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          uploadedMediaImages.push(result.value.image);
          if (result.value.roomAssignment) {
            newUploadedRoomAssignments.push(result.value.roomAssignment);
          }
          return;
        }

        if (result.reason instanceof Error) {
          reportError(result.reason);
        }
      });

      const failedUploadCount = uploadResults.length - uploadedMediaImages.length;

      if (uploadedMediaImages.length > 0) {
        setUploadedImages((currentImages) => dedupeMediaImages(currentImages.concat(uploadedMediaImages)));
        setUploadedRoomAssignments((currentAssignments) => currentAssignments.concat(newUploadedRoomAssignments));
        enqueueSnackbar(
          `${uploadedMediaImages.length} ${uploadedMediaImages.length === 1 ? 'image' : 'images'} uploaded`,
          { variant: 'success' },
        );
      }

      if (failedUploadCount > 0) {
        enqueueSnackbar(`${failedUploadCount} ${failedUploadCount === 1 ? 'image' : 'images'} failed to upload`, {
          variant: 'error',
        });
      }
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Failed to upload images'), { variant: 'error' });
      if (error instanceof Error) {
        reportError(error);
      }
    } finally {
      setIsUploadingImages(false);
    }
  };

  const uploadFilesToRoom = (roomId: string, roomName: string, files: Array<File>) =>
    uploadFiles(files, { roomAssignment: { roomId, roomName } });

  const assignImagesToRoom = (roomId: string, roomName: string, selectedImages: Array<MediaImage>) => {
    setImageRoomOverrides((currentRooms) => {
      const nextRooms = { ...currentRooms };

      selectedImages.forEach((image) => {
        nextRooms[image.publicId] = { roomId, roomName };
      });

      return nextRooms;
    });
    setImageEdits((currentEdits) => {
      const nextEdits = { ...currentEdits };

      selectedImages.forEach((image) => {
        if (nextEdits[image.publicId]) {
          nextEdits[image.publicId] = {
            ...nextEdits[image.publicId],
            roomId,
            roomName,
          };
        }
      });

      return nextEdits;
    });
  };

  const deletePersistedRoomImage = (image: MediaImage, successMessage: string, errorMessage: string) => {
    if (!canPersistRoomImageHiddenState(image)) {
      return;
    }

    const existingDeletionPromise = roomImageDeletionPromisesRef.current.get(image.publicId);

    if (existingDeletionPromise) {
      return existingDeletionPromise;
    }

    const deletionPromise = ReservationService.deleteRoomTypeImage(
      image.sourceImageId,
      image.sourcePropertyId,
      image.sourceRoomTypeId,
    )
      .then(() => {
        enqueueSnackbar(successMessage, { variant: 'success' });
      })
      .catch((error) => {
        if (error instanceof Error) {
          reportError(error);
        }
        enqueueSnackbar(getErrorMessage(error, errorMessage), { variant: 'error' });
        throw error;
      })
      .finally(() => {
        roomImageDeletionPromisesRef.current.delete(image.publicId);
      });

    roomImageDeletionPromisesRef.current.set(image.publicId, deletionPromise);

    return deletionPromise;
  };

  const removeImageFromRoom = async (publicId: string) => {
    const image = mediaImages.find((mediaImage) => mediaImage.publicId === publicId);
    const persistedRoomImageDeletion = image
      ? deletePersistedRoomImage(image, 'Room image removed', 'Failed to remove room image')
      : undefined;

    if (persistedRoomImageDeletion) {
      await persistedRoomImageDeletion;
      setRemovedRoomImagePublicIds((currentPublicIds) =>
        currentPublicIds.includes(publicId) ? currentPublicIds : currentPublicIds.concat(publicId),
      );
    }

    setImageRoomOverrides((currentRooms) => ({
      ...currentRooms,
      [publicId]: UNASSIGNED_ROOM_OPTION,
    }));
    setHiddenImageOverrides((currentOverrides) => removeImageOverride(currentOverrides, publicId));
    setImageEdits((currentEdits) =>
      currentEdits[publicId]
        ? {
            ...currentEdits,
            [publicId]: {
              ...currentEdits[publicId],
              roomId: undefined,
              roomName: DEFAULT_ROOM_NAME,
            },
          }
        : currentEdits,
    );
  };

  const deleteImage = async (publicId: string) => {
    const image = mediaImages.find((mediaImage) => mediaImage.publicId === publicId);
    const persistedRoomImageDeletion = image
      ? deletePersistedRoomImage(image, 'Room image deleted', 'Failed to delete room image')
      : undefined;

    if (persistedRoomImageDeletion) {
      await persistedRoomImageDeletion;
    }

    setDeletedImagePublicIds((currentPublicIds) =>
      currentPublicIds.includes(publicId) ? currentPublicIds : currentPublicIds.concat(publicId),
    );
    setHiddenImageOverrides((currentOverrides) => removeImageOverride(currentOverrides, publicId));
    setImageRoomOverrides((currentRooms) => {
      const nextRooms = { ...currentRooms };

      delete nextRooms[publicId];

      return nextRooms;
    });
    setImageEdits((currentEdits) => {
      const nextEdits = { ...currentEdits };

      delete nextEdits[publicId];

      return nextEdits;
    });
  };

  const toggleHiddenImage = async (publicId: string) => {
    const image = mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (!image) {
      return;
    }

    const persistentImage = canPersistRoomImageHiddenState(image) ? image : undefined;

    if (persistentImage) {
      if (hiddenImageUpdatePublicIdsRef.current.has(publicId)) {
        return;
      }

      hiddenImageUpdatePublicIdsRef.current.add(publicId);
    }

    const previousHiddenOverride = hiddenImageOverrides[publicId];
    const nextHidden = !(previousHiddenOverride ?? image.hidden ?? false);

    setHiddenImageOverrides((currentOverrides) => ({
      ...currentOverrides,
      [publicId]: nextHidden,
    }));

    if (!persistentImage) {
      return;
    }

    try {
      await ReservationService.updateRoomTypeImage(
        {
          id_cloudinary_external: persistentImage.publicId,
          hidden: nextHidden,
        },
        persistentImage.sourceImageId,
        persistentImage.sourcePropertyId,
        persistentImage.sourceRoomTypeId,
      );
      enqueueSnackbar(nextHidden ? 'Room image hidden' : 'Room image shown', { variant: 'success' });
    } catch (error) {
      setHiddenImageOverrides((currentOverrides) =>
        previousHiddenOverride === undefined
          ? removeImageOverride(currentOverrides, publicId)
          : { ...currentOverrides, [publicId]: previousHiddenOverride },
      );
      if (error instanceof Error) {
        reportError(error);
      }
      enqueueSnackbar(getErrorMessage(error, 'Failed to update room image visibility'), { variant: 'error' });
    } finally {
      hiddenImageUpdatePublicIdsRef.current.delete(publicId);
    }
  };

  const editRoomImage = (publicId: string) => {
    const image = mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (image) {
      setRoomImagePendingEdit(image);
    }
  };

  const deleteRoomImageFromEditor = (image: MediaImage) => {
    setImagePendingDeletion(image);
  };

  const saveImageEdit = (publicId: string, edit: LocalImageEdit) => {
    setImageEdits((currentEdits) => ({
      ...currentEdits,
      [publicId]: edit,
    }));
    setImageRoomOverrides((currentRooms) => ({
      ...currentRooms,
      [publicId]: edit.roomId ? { roomId: edit.roomId, roomName: edit.roomName } : UNASSIGNED_ROOM_OPTION,
    }));
  };

  const updateImageTitle = (publicId: string, title: string) => {
    const image = mediaImages.find((mediaImage) => mediaImage.publicId === publicId);

    if (!image) {
      return;
    }

    setImageEdits((currentEdits) => ({
      ...currentEdits,
      [publicId]: {
        ...(currentEdits[publicId] ?? buildDefaultEdit(image, roomOptions)),
        title,
      },
    }));
  };

  const closeDeleteDialog = () => {
    setImagePendingDeletion(undefined);
  };

  const confirmDeleteImage = async () => {
    if (!imagePendingDeletion) {
      return;
    }

    try {
      await deleteImage(imagePendingDeletion.publicId);
    } catch {
      return;
    }

    setRoomImagePendingEdit((currentImage) =>
      currentImage?.publicId === imagePendingDeletion.publicId ? undefined : currentImage,
    );
    setImagePendingDeletion(undefined);
  };

  return {
    deletedImagePublicIds,
    hiddenImagePublicIds,
    imageEdits,
    imagePendingDeletion,
    isUploadingImages,
    mediaImages,
    roomImagePendingEdit,
    roomOptions,
    uploadedRoomAssignments,
    videoId,
    closeDeleteDialog,
    confirmDeleteImage,
    deleteImage,
    deleteRoomImageFromEditor,
    removeImageFromRoom,
    saveImageEdit,
    setRoomImagePendingEdit,
    setVideoId: setLocalVideoId,
    toggleHiddenImage,
    updateImageTitle,
    uploadFiles,
    uploadFilesToRoom,
    assignImagesToRoom,
    editRoomImage,
  };
}

function findPersistedRoomByAssignment(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages> | undefined,
  roomAssignment: UploadRoomAssignment,
) {
  return propertyRoomTypes?.find((room, index) => getRoomKey(room, index) === roomAssignment.roomId && room.id);
}

async function createPersistedRoomImage({
  propertyRoomTypes,
  publicId,
  roomAssignment,
  uploadIndex,
}: {
  propertyRoomTypes: Array<PropertyRoomTypeWithImages> | undefined;
  publicId: string;
  roomAssignment: UploadRoomAssignment;
  uploadIndex: number;
}) {
  const room = findPersistedRoomByAssignment(propertyRoomTypes, roomAssignment);
  const propertyId = room?.property_id;
  const roomTypeId = room?.id;

  if (!propertyId || !roomTypeId) {
    return undefined;
  }

  const roomImage = await ReservationService.createRoomTypeImage(
    {
      hidden: false,
      id_cloudinary_external: publicId,
      order: (room.images?.length ?? 0) + uploadIndex + 1,
    },
    propertyId,
    roomTypeId,
  );

  return { ...roomImage, sourcePropertyId: propertyId, sourceRoomTypeId: roomTypeId };
}
